export type RangeId = 'today' | 'tomorrow' | 'week'

export type DeltaTone = 'good' | 'bad' | 'neutral'

export type ActionTag = 'Storage' | 'Market' | 'Demand' | 'Assets'

export interface LoadPoint {
    label: string
    forecast: number
    actual: number | null
    low: number
    high: number
}

export interface KpiItem {
    id: string
    label: string
    value: string
    unit: string
    delta: string
    tone: DeltaTone
    direction?: 'up' | 'down'
    trend?: number[]
    meter?: {
        value: number
        max: number
    }
}

export interface MixItem {
    id: 'wind' | 'solar' | 'hydro' | 'other'
    label: string
    share: number
}

export interface ActionItem {
    id: string
    title: string
    detail: string
    tag: ActionTag
}

export interface DashboardRange {
    id: RangeId
    label: string
    chartSubtitle: string
    tickEvery: number
    nowIndex: number | null
    points: LoadPoint[]
    kpis: KpiItem[]
    mix: MixItem[]
    actions: ActionItem[]
}

const hours = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`)

const todayForecast = [1860, 1760, 1690, 1650, 1670, 1780, 2010, 2260, 2410, 2440, 2380, 2290, 2220, 2190, 2260, 2410, 2600, 2810, 2914, 2860, 2690, 2470, 2240, 2020]

const todayActual = [1842, 1771, 1702, 1638, 1661, 1795, 2034, 2241, 2436, 2461, 2352, 2268, 2231, 2176, 2243]

const tomorrowForecast = [1900, 1800, 1720, 1680, 1700, 1820, 2060, 2310, 2470, 2500, 2430, 2330, 2250, 2210, 2290, 2450, 2660, 2880, 2990, 3020, 2840, 2580, 2330, 2090]

const weekDays = ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu']

const weekForecast = [2914, 3020, 2550, 2680, 3080, 2970, 3010]

const withBand = (value: number, spread: number): Pick<LoadPoint, 'low' | 'high'> => ({
    low: Math.round(value * (1 - spread)),
    high: Math.round(value * (1 + spread))
})

const TODAY_NOW = 14

const todayPoints: LoadPoint[] = todayForecast.map((forecast, index) => ({
    label: hours[index],
    forecast,
    actual: todayActual[index] ?? null,
    ...withBand(forecast, index <= TODAY_NOW ? 0.02 : 0.02 + 0.004 * (index - TODAY_NOW))
}))

const tomorrowPoints: LoadPoint[] = tomorrowForecast.map((forecast, index) => ({
    label: hours[index],
    forecast,
    actual: null,
    ...withBand(forecast, 0.03 + 0.0012 * index)
}))

const weekPoints: LoadPoint[] = weekForecast.map((forecast, index) => ({
    label: weekDays[index],
    forecast,
    actual: null,
    ...withBand(forecast, 0.02 + 0.006 * index)
}))

export const dashboardRanges: DashboardRange[] = [
    {
        id: 'today',
        label: 'Today',
        chartSubtitle: 'Hourly load in MW, actuals up to 14:00',
        tickEvery: 3,
        nowIndex: TODAY_NOW,
        points: todayPoints,
        kpis: [
            {
                id: 'peak',
                label: 'Forecasted peak load',
                value: '2,914',
                unit: 'MW',
                delta: '+4.2% vs last Friday',
                tone: 'neutral',
                direction: 'up',
                trend: [2610, 2680, 2720, 2650, 2700, 2790, 2760, 2810, 2850, 2790, 2880, 2914]
            },
            {
                id: 'curtailment',
                label: 'Curtailment risk',
                value: '12',
                unit: '%',
                delta: '3 pts lower than yesterday',
                tone: 'good',
                direction: 'down',
                trend: [19, 18, 18, 17, 16, 16, 15, 15, 14, 13, 13, 12]
            },
            {
                id: 'storage',
                label: 'Available storage',
                value: '680',
                unit: 'MWh',
                delta: '74% of 920 MWh capacity',
                tone: 'neutral',
                meter: { value: 680, max: 920 }
            },
            {
                id: 'price',
                label: 'Average spot price',
                value: '€84.20',
                unit: '/MWh',
                delta: '€6.10 higher than yesterday',
                tone: 'good',
                direction: 'up',
                trend: [71, 73, 72, 75, 77, 76, 79, 80, 78, 81, 83, 84.2]
            }
        ],
        mix: [
            { id: 'wind', label: 'Wind', share: 41 },
            { id: 'solar', label: 'Solar', share: 33 },
            { id: 'hydro', label: 'Hydro', share: 14 },
            { id: 'other', label: 'Other', share: 12 }
        ],
        actions: [
            {
                id: 'charge-ridgeway',
                title: 'Charge Ridgeway battery',
                detail: '13:00 to 15:00, absorbs 186 MWh of solar surplus',
                tag: 'Storage'
            },
            {
                id: 'bid-evening-peak',
                title: 'Bid 60 MW into the evening peak',
                detail: '18:00 to 20:00 at an expected €118/MWh',
                tag: 'Market'
            },
            {
                id: 'precool-harbor',
                title: 'Pre-cool the Harbor district',
                detail: '16:30 to 17:30, trims 42 MW off the peak',
                tag: 'Demand'
            }
        ]
    },
    {
        id: 'tomorrow',
        label: 'Tomorrow',
        chartSubtitle: 'Hourly load forecast in MW',
        tickEvery: 3,
        nowIndex: null,
        points: tomorrowPoints,
        kpis: [
            {
                id: 'peak',
                label: 'Forecasted peak load',
                value: '3,020',
                unit: 'MW',
                delta: '+3.6% vs today, at 19:00',
                tone: 'neutral',
                direction: 'up',
                trend: [2700, 2740, 2690, 2780, 2810, 2760, 2850, 2880, 2840, 2914, 2960, 3020]
            },
            {
                id: 'curtailment',
                label: 'Curtailment risk',
                value: '9',
                unit: '%',
                delta: '3 pts lower than today',
                tone: 'good',
                direction: 'down',
                trend: [16, 15, 15, 14, 14, 13, 12, 12, 11, 10, 10, 9]
            },
            {
                id: 'storage',
                label: 'Available storage',
                value: '742',
                unit: 'MWh',
                delta: '81% of 920 MWh capacity',
                tone: 'neutral',
                meter: { value: 742, max: 920 }
            },
            {
                id: 'price',
                label: 'Average spot price',
                value: '€91.40',
                unit: '/MWh',
                delta: '€7.20 higher than today',
                tone: 'good',
                direction: 'up',
                trend: [76, 78, 77, 80, 82, 81, 84, 86, 85, 88, 90, 91.4]
            }
        ],
        mix: [
            { id: 'wind', label: 'Wind', share: 47 },
            { id: 'solar', label: 'Solar', share: 28 },
            { id: 'hydro', label: 'Hydro', share: 13 },
            { id: 'other', label: 'Other', share: 12 }
        ],
        actions: [
            {
                id: 'hold-ridgeway',
                title: 'Hold Ridgeway battery at 80%',
                detail: 'Until 17:00, reserved for the 19:00 peak',
                tag: 'Storage'
            },
            {
                id: 'bid-tomorrow-evening',
                title: 'Bid 85 MW into 18:00 to 21:00',
                detail: 'Expected clearing price of €126/MWh',
                tag: 'Market'
            },
            {
                id: 'service-kestrel',
                title: 'Service the Kestrel Ridge turbines',
                detail: '05:00 to 08:00, the calmest wind of the day',
                tag: 'Assets'
            }
        ]
    },
    {
        id: 'week',
        label: 'Next 7 days',
        chartSubtitle: 'Daily peak load forecast in MW',
        tickEvery: 1,
        nowIndex: null,
        points: weekPoints,
        kpis: [
            {
                id: 'peak',
                label: 'Highest peak this week',
                value: '3,080',
                unit: 'MW',
                delta: 'Tuesday evening',
                tone: 'neutral',
                trend: [2914, 3020, 2550, 2680, 3080, 2970, 3010]
            },
            {
                id: 'curtailment',
                label: 'Curtailment risk',
                value: '14',
                unit: '%',
                delta: '2 pts higher than last week',
                tone: 'bad',
                direction: 'up',
                trend: [11, 12, 12, 13, 12, 13, 14]
            },
            {
                id: 'storage',
                label: 'Average available storage',
                value: '615',
                unit: 'MWh',
                delta: '67% of 920 MWh capacity',
                tone: 'neutral',
                meter: { value: 615, max: 920 }
            },
            {
                id: 'price',
                label: 'Average spot price',
                value: '€79.80',
                unit: '/MWh',
                delta: '€4.40 lower than last week',
                tone: 'bad',
                direction: 'down',
                trend: [86, 85, 83, 82, 80, 81, 79.8]
            }
        ],
        mix: [
            { id: 'wind', label: 'Wind', share: 44 },
            { id: 'solar', label: 'Solar', share: 30 },
            { id: 'hydro', label: 'Hydro', share: 15 },
            { id: 'other', label: 'Other', share: 11 }
        ],
        actions: [
            {
                id: 'move-maintenance',
                title: 'Move maintenance to Sunday',
                detail: 'Lowest peak of the week at 2,550 MW',
                tag: 'Assets'
            },
            {
                id: 'lock-tuesday',
                title: 'Lock in Tuesday evening',
                detail: 'Forward bid for 70 MW at €131/MWh',
                tag: 'Market'
            },
            {
                id: 'tighten-alerts',
                title: 'Tighten curtailment alerts',
                detail: 'Solar surplus expected on Sunday and Monday',
                tag: 'Storage'
            }
        ]
    }
]
